import React, { useEffect, useState } from 'react'
import { Typography, Tabs, Tab, CircularProgress, Box } from '@mui/material'
import ListEditor from '../common/ListEditor'
import useDropdowns from '../hooks/api/useDropdowns'
import useBusinessTypes from '../hooks/api/useBusinessTypes'
import useCompanyConfig from '../hooks/api/useCompanyConfig'
import SettingsFooter from './shared/SettingsFooter'
import useAlert from '../hooks/useAlert'
import GenericLoadError from '../common/GenericLoadError'
import { useSearchParams } from 'react-router-dom'
import { grey, blue, green, yellow } from '@mui/material/colors'
import useIsMobile, { useScreenSize } from '../hooks/useIsMobile'
import DragAndDropInstructions from './shared/DragAndDropInstructions'
import TabsOrMobileDropdown from '../common/TabsOrMobileDropdown'

export const DropdownsForm = ({
  dropdownSummary,
  tab,
  setTab,
  updateDropdown,
}) => {
  const { description, options, isRequired } = dropdownSummary[tab]

  const isMobile = useIsMobile()
  return (
    <>
      {isMobile && (
        <TabsOrMobileDropdown
          tabs={dropdownSummary.map((d) => ({ label: d.pluralName }))}
          setTab={setTab}
          tab={tab}
        ></TabsOrMobileDropdown>
      )}
      <Box sx={{ display: 'flex', width: '100%' }}>
        {!isMobile && (
          <Tabs
            orientation='vertical'
            sx={{
              my: 2,
              flexShrink: 0,
              maxHeight: '400px',
              backgroundColor: 'white',
              border: `1px solid ${grey[300]}`,
            }}
            value={tab}
            onChange={(_e, val) => setTab(val)}
            variant='scrollable'
          >
            {dropdownSummary.map((d, i) => {
              return (
                <Tab
                  sx={{
                    alignItems: 'start',
                    borderTop: i === 0 ? null : `1px solid ${grey[200]}`,
                    backgroundColor: tab === i ? blue[50] : null,
                  }}
                  key={d.key}
                  label={d.pluralName}
                ></Tab>
              )
            })}
          </Tabs>
        )}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            justifyContent: 'center',
            ml: 2,
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              maxWidth: '800px',
            }}
          >
            <Typography
              sx={{
                my: 2,
                textAlign: 'center',

                fontSize: '20px',
              }}
            >
              {description}
            </Typography>

            <DragAndDropInstructions sx={{ mb: 3, textAlign: 'center' }} />
            <ListEditor
              maxLength={200} //
              list={options || []}
              editableKeys={[{ label: 'Name', key: 'name' }]}
              onChange={(value) => updateDropdown(value, tab)}
              getCanRemove={() => true}
              getCanEdit={() => true}
              getAltText={() => null}
              emptyItem={{ name: '' }}
              canReorder
              canAddOnEnter
            ></ListEditor>
            {isRequired && options.length === 0 && (
              <Typography sx={{ textAlign: 'center', my: 2 }} color={'error'}>
                <i>You must choose at least one to continue</i>
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  )
}

const Dropdowns = () => {
  const [params, setParams] = useSearchParams()

  const [tab, setTab] = useState(0)
  const { businessTypes, ...bt } = useBusinessTypes()
  const { companyConfig, ...cc } = useCompanyConfig()

  const {
    dropdownsSummary,
    dropdownsSummaryCopy,
    setDropdownsSummary,
    filterSummary,
    saveDropdowns,
    reset,
    ...dd
  } = useDropdowns({ noFallback: true })

  useEffect(() => {
    if (!dropdownsSummary?.length) return
    const tabParam = params.get('tab')
    if (!tabParam) return

    const match = dropdownsSummary.find((d) => d.key === tabParam)

    if (!match) return
    const index = dropdownsSummary.indexOf(match)

    setTab(index)
  }, [dropdownsSummary, params])

  const { createAlert, createGenericErrorAlert } = useAlert()

  const isLoading = bt.isLoading || dd.isLoading || cc.isLoading
  const hasLoadError = bt.hasLoadError || dd.hasLoadError || cc.hasLoadError

  const updateDropdown = (options, i) => {
    const newArr = [...dropdownsSummary]
    newArr[i] = { ...newArr[i], options }
    setDropdownsSummary(newArr)
  }

  const onSuccess = () =>
    createAlert({
      severity: 'success',
      message: 'Successfully saved Dropdowns',
    })

  const onFailure = () => createGenericErrorAlert()

  const { windowWidth } = useScreenSize()

  if (isLoading) return <CircularProgress />
  if (hasLoadError) return <GenericLoadError />

  const hasRequiredDropdownWithNoOptions = dropdownsSummary.some(
    (dd) => dd.isRequired && dd.options.length === 0
  )

  return (
    <Box
      sx={{
        flexGrow: 1,
        // mr: windowWidth < 1000 ? 4 : '200px'
      }}
    >
      <DropdownsForm
        dropdownSummary={dropdownsSummary}
        tab={tab}
        setTab={(...args) => {
          params.delete('tab')
          setParams(params)
          setTab(...args)
        }}
        updateDropdown={updateDropdown}
      />
      <SettingsFooter
        saveButton={{
          onClick: () => saveDropdowns().then(onSuccess).catch(onFailure),
          disabled: hasRequiredDropdownWithNoOptions,
        }}
        cancelButton={{ onClick: () => reset(), label: 'Reset' }}
      />
    </Box>
  )
}

export default Dropdowns
